import { useContext } from 'react'
import useSWR from 'swr'
import { api } from '../services/api'
import { AccountContext } from '../contexts/AccountContext'

const fetcher = async (_key, accountId) => {
  return api.getAccountSubscription(accountId)
}

/**
 * Central hook for accessing account-level subscription data.
 * Returns null SWR key (disabling fetch) until accountId is provided.
 *
 * @param {string|null} accountId - Instagram account ID (required for data fetch)
 * @returns {Object} Subscription data and SWR state
 */
export function useSubscription(accountId = null) {
  const swrKey = accountId ? ['subscription', accountId] : null

  const { data, error, mutate } = useSWR(swrKey, () => fetcher(swrKey[0], accountId), {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 30000, // 30 seconds - reduced for faster updates
    refreshInterval: 60000, // 1 minute - reduced from 5 minutes for faster quota updates
  })

  return {
    plan: data?.plan || 'free',
    status: data?.status || 'inactive',
    hasStripeCustomer: data?.has_stripe_customer || false,
    quotas: data?.quotas || {
      stories: { used: 0, limit: 0, remaining: 0, unlimited: false },
      regenerations: { used: 0, limit: 0, remaining: 0, unlimited: false },
      ecommerce_stories: { used: 0, limit: 0, remaining: 0, unlimited: false },
    },
    features: data?.features || {
      video_upload: false,
      languages: ['en'],
    },
    billingCycle: data?.billing_cycle || {
      type: 'monthly',
      start: null,
      end: null,
      resets_at: null,
    },
    limits: data?.limits || {
      min_post_interval_hours: 24,
    },
    loading: !error && !data,
    error,
    mutate,
  }
}

/**
 * Hook to check if an account has access to a specific feature.
 *
 * @param {string} featureId - Feature identifier (e.g., 'video_upload', 'language_de')
 * @param {string|null} accountId - Instagram account ID
 * @returns {{ hasFeature: boolean, loading: boolean }}
 */
export function useHasFeature(featureId, accountId = null) {
  const { features, loading } = useSubscription(accountId)

  // Handle special cases
  if (featureId === 'video_upload') {
    return {
      hasFeature: features.video_upload === true,
      loading,
    }
  }

  // Handle language features
  if (featureId.startsWith('language_')) {
    const languageCode = featureId.replace('language_', '')
    return {
      hasFeature: features.languages?.includes(languageCode) || false,
      loading,
    }
  }

  // Default to checking boolean features
  return {
    hasFeature: features[featureId] === true,
    loading,
  }
}

/**
 * Hook to check quota status for a specific quota type.
 *
 * @param {string} quotaType - Quota type ('stories', 'regenerations', or 'ecommerce_stories')
 * @param {string|null} accountId - Instagram account ID
 * @returns {{ used: number, limit: number, remaining: number, unlimited: boolean, percentage: number, isNearLimit: boolean, isExceeded: boolean, loading: boolean }}
 */
export function useQuotaStatus(quotaType, accountId = null) {
  const { quotas, loading } = useSubscription(accountId)

  const quota = quotas[quotaType] || {
    used: 0,
    limit: 0,
    remaining: 0,
    unlimited: false,
  }

  const percentage = quota.unlimited
    ? 0
    : quota.limit > 0
      ? Math.round((quota.used / quota.limit) * 100)
      : 0

  const isNearLimit = !quota.unlimited && percentage >= 80
  const isExceeded = !quota.unlimited && quota.used >= quota.limit

  return {
    used: quota.used,
    limit: quota.limit,
    remaining: quota.remaining,
    unlimited: quota.unlimited,
    percentage,
    isNearLimit,
    isExceeded,
    loading,
  }
}

/**
 * Helper hook to get the currently selected account ID from AccountContext.
 * @returns {string|null} Currently selected account ID or null
 */
export function useSelectedAccountId() {
  const context = useContext(AccountContext)
  return context?.selectedAccountId || null
}

/**
 * Invalidate subscription cache and force immediate refresh.
 * Call after actions that affect quotas (uploads, etc.)
 *
 * @param {string} accountId - Instagram account ID
 */
export async function invalidateSubscriptionCache(accountId) {
  const { mutate } = await import('swr')
  const swrKey = accountId ? ['subscription', accountId] : null
  if (swrKey) {
    await mutate(swrKey, undefined, { revalidate: true })
  }
}
